import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Upload, Download, FileText, FolderOpen, Trash2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient, getApiBaseUrl, joinUrl } from "@/lib/queryClient";
import { Progress } from "@/components/ui/progress";

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  webViewLink: string;
  webContentLink: string;
  createdTime: string;
  folder: 'candidate' | 'employee';
}

export default function AIAgent() {
  const { toast } = useToast();
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadFolder, setUploadFolder] = useState<'candidate' | 'employee'>('candidate');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch uploaded files
  const { data: files, isLoading: filesLoading, refetch: refetchFiles, error: filesError } = useQuery({
    queryKey: ['/ai-agent/files'],
    queryFn: async () => {
      const response = await apiRequest("GET", `/ai-agent/files?t=${Date.now()}`);
      return response.json();
    },
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache the data
  });

  // Upload files mutation
  const uploadFilesMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await apiRequest("POST", "/ai-agent/upload", formData);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Files uploaded successfully",
      });
      setSelectedFiles(null);
      setUploadProgress(0);
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Force complete refresh of the cache
      queryClient.removeQueries({ queryKey: ['/ai-agent/files'] });
      queryClient.invalidateQueries({ queryKey: ['/ai-agent/files'] });
      // Also refetch immediately
      setTimeout(() => {
        refetchFiles();
      }, 100);
    },
    onError: (error: Error) => {
      // Check if it's a permission error
      if (error.message.includes('Google Drive access not configured')) {
        toast({
          title: "Drive Access Required",
          description: "Please grant Google Drive permissions to upload files",
          variant: "destructive",
          action: {
            label: "Grant Access",
            onClick: () => {
              const baseUrl = getApiBaseUrl();
              const driveAuthUrl = baseUrl ? joinUrl(baseUrl, '/auth/google/drive') : '/auth/google/drive';
              window.location.href = driveAuthUrl;
            }
          }
        });
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
      setIsUploading(false);
      setUploadProgress(0);
    },
  });

  // Delete file mutation
  const deleteFileMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const response = await apiRequest("DELETE", `/ai-agent/files/${fileId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "File deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/ai-agent/files'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select files to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('folder', uploadFolder);
    
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append('files', selectedFiles[i]);
    }

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 200);

    try {
      await uploadFilesMutation.mutateAsync(formData);
    } catch (error) {
      clearInterval(progressInterval);
    }
  };

  const handleDownload = (file: UploadedFile) => {
    window.open(file.webContentLink, '_blank');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const candidateFiles = (files as any)?.files?.filter((file: UploadedFile) => file.folder === 'candidate') || [];
  const employeeFiles = (files as any)?.files?.filter((file: UploadedFile) => file.folder === 'employee') || [];

  return (
    <div className="space-y-8" data-testid="ai-agent-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-servify-dark-grey">AI Agent</h1>
          <p className="text-servify-dark-grey/70 mt-2">Upload and manage Q&A documents to Google Drive</p>
        </div>
        <Button
          onClick={() => refetchFiles()}
          variant="outline"
          disabled={filesLoading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${filesLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Upload Section */}
      <Card className="border-servify-white-grey shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Files
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="folder-select" className="text-sm font-medium">
                Select Folder
              </Label>
              <Select value={uploadFolder} onValueChange={(value: 'candidate' | 'employee') => setUploadFolder(value)}>
                <SelectTrigger className="w-full h-10">
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white border-2 border-servify-purple/30 rounded-lg shadow-xl">
                  <SelectItem value="candidate" className="hover:bg-servify-purple/10 cursor-pointer text-servify-dark-grey">Candidate</SelectItem>
                  <SelectItem value="employee" className="hover:bg-servify-purple/10 cursor-pointer text-servify-dark-grey">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file-upload" className="text-sm font-medium">
                Select Files
              </Label>
              <Input
                id="file-upload"
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="h-10"
                accept=".doc,.docx"
              />
              <p className="text-sm text-servify-dark-grey/70">
                Supported formats: DOC, DOCX (Questions and Answers documents)
              </p>
            </div>

            {selectedFiles && selectedFiles.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium">Selected Files:</p>
                <div className="space-y-2">
                  {Array.from(selectedFiles).map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-servify-white-grey rounded-lg border">
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-servify-purple flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-servify-dark-grey truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-servify-dark-grey/70">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isUploading && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Uploading files...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="w-full" />
              </div>
            )}

            <div className="pt-2">
              <Button
                onClick={handleUpload}
                disabled={!selectedFiles || selectedFiles.length === 0 || isUploading}
                className="w-full h-11 bg-servify-purple hover:bg-servify-purple-dark text-white"
              >
                <Upload className="mr-2 h-4 w-4" />
                {isUploading ? 'Uploading...' : 'Upload Files'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Files Display */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Candidate Files */}
        <Card className="border-servify-white-grey shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-servify-purple" />
              AI Agent/Candidate Files
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filesLoading ? (
              <div className="text-center py-8">Loading files...</div>
            ) : filesError ? (
              <div className="text-center py-8 space-y-4">
                <div className="text-servify-dark-grey/70">
                  <p className="font-medium">Google Drive access required</p>
                  <p className="text-sm mt-1">To access AI Agent files, you need to grant Google Drive permissions.</p>
                </div>
                <Button
                  onClick={() => {
                    const baseUrl = getApiBaseUrl();
                    const driveAuthUrl = baseUrl ? joinUrl(baseUrl, '/auth/google/drive') : '/auth/google/drive';
                    window.location.href = driveAuthUrl;
                  }}
                  className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                >
                  Grant Drive Access
                </Button>
              </div>
            ) : candidateFiles.length > 0 ? (
              <div className="space-y-3">
                {candidateFiles.map((file: UploadedFile) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-servify-white-grey rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-servify-purple flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-servify-dark-grey truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-servify-dark-grey/70">
                          {formatFileSize(file.size)} • {formatDate(file.createdTime)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(file)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteFileMutation.mutate(file.id)}
                        disabled={deleteFileMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-servify-purple" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-servify-dark-grey/50 py-8">
                No files uploaded to candidate folder yet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Employee Files */}
        <Card className="border-servify-white-grey shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-servify-purple" />
              AI Agent/Employee Files
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filesLoading ? (
              <div className="text-center py-8">Loading files...</div>
            ) : filesError ? (
              <div className="text-center py-8 space-y-4">
                <div className="text-servify-dark-grey/70">
                  <p className="font-medium">Google Drive access required</p>
                  <p className="text-sm mt-1">To access AI Agent files, you need to grant Google Drive permissions.</p>
                </div>
                <Button
                  onClick={() => {
                    const baseUrl = getApiBaseUrl();
                    const driveAuthUrl = baseUrl ? joinUrl(baseUrl, '/auth/google/drive') : '/auth/google/drive';
                    window.location.href = driveAuthUrl;
                  }}
                  className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                >
                  Grant Drive Access
                </Button>
              </div>
            ) : employeeFiles.length > 0 ? (
              <div className="space-y-3">
                {employeeFiles.map((file: UploadedFile) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-servify-white-grey rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-servify-purple flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-servify-dark-grey truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-servify-dark-grey/70">
                          {formatFileSize(file.size)} • {formatDate(file.createdTime)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(file)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteFileMutation.mutate(file.id)}
                        disabled={deleteFileMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-servify-purple" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-servify-dark-grey/50 py-8">
                No files uploaded to employee folder yet
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
